import React, { useState } from 'react';
import { signIn, signUp, confirmSignUp, User } from '../utils/auth';

interface LoginProps {
  onLogin: (user: User) => void;
}

export default function Login({ onLogin }: LoginProps) {
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');

  const [signInForm, setSignInForm] = useState({
    email: '',
    password: ''
  });

  const [signUpForm, setSignUpForm] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const user = await signIn(signInForm.email, signInForm.password);
      onLogin(user);
    } catch (err) {
      setError((err as Error).message || 'ログインに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (signUpForm.password !== signUpForm.confirmPassword) {
        throw new Error('パスワードが一致しません');
      }

      await signUp(signUpForm.email, signUpForm.password);
      setPendingEmail(signUpForm.email);
      setShowConfirmation(true);
      setSuccess('メールに送信された確認コードを入力してください');
    } catch (err) {
      setError((err as Error).message || 'サインアップに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await confirmSignUp(pendingEmail, confirmationCode);
      setSuccess('アカウントが確認されました。サインインしてください');
      setShowConfirmation(false);
      setActiveTab('signin');
      setConfirmationCode('');
    } catch (err) {
      setError((err as Error).message || '確認に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(to bottom right, #3b82f6, #1d4ed8)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
        width: '100%',
        maxWidth: '28rem'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>
            財務分析システム
          </h1>
          <p style={{ color: '#6b7280' }}>日本企業の財務データ分析・可視化システム</p>
        </div>

        <div style={{ display: 'flex', marginBottom: '1.5rem', backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '0.25rem' }}>
          <button
            onClick={() => setActiveTab('signin')}
            style={{
              flex: 1,
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: '500',
              fontSize: '0.875rem',
              transition: 'all 200ms',
              backgroundColor: activeTab === 'signin' ? 'white' : 'transparent',
              boxShadow: activeTab === 'signin' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
              color: activeTab === 'signin' ? '#111827' : '#6b7280',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => setActiveTab('signup')}
            style={{
              flex: 1,
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: '500',
              fontSize: '0.875rem',
              transition: 'all 200ms',
              backgroundColor: activeTab === 'signup' ? 'white' : 'transparent',
              boxShadow: activeTab === 'signup' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
              color: activeTab === 'signup' ? '#111827' : '#6b7280',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Sign Up
          </button>
        </div>

        {error && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            color: '#15803d',
            borderRadius: '4px'
          }}>
            {success}
          </div>
        )}

        {activeTab === 'signin' && (
          <form onSubmit={handleSignIn}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                メールアドレス
              </label>
              <input
                type="email"
                value={signInForm.email}
                onChange={(e) => setSignInForm({...signInForm, email: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 200ms'
                }}
                placeholder="example@email.com"
                required
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                パスワード
              </label>
              <input
                type="password"
                value={signInForm.password}
                onChange={(e) => setSignInForm({...signInForm, password: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="パスワードを入力"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: loading ? '#93c5fd' : '#2563eb',
                color: 'white',
                fontWeight: 'bold',
                padding: '0.75rem 1rem',
                borderRadius: '6px',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 200ms'
              }}
            >
              {loading ? 'サインイン中...' : 'サインイン'}
            </button>
          </form>
        )}

        {activeTab === 'signup' && (
          <form onSubmit={handleSignUp}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                メールアドレス
              </label>
              <input
                type="email"
                value={signUpForm.email}
                onChange={(e) => setSignUpForm({...signUpForm, email: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="example@email.com"
                required
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                パスワード
              </label>
              <input
                type="password"
                value={signUpForm.password}
                onChange={(e) => setSignUpForm({...signUpForm, password: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="パスワードを入力"
                required
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                パスワード確認
              </label>
              <input
                type="password"
                value={signUpForm.confirmPassword}
                onChange={(e) => setSignUpForm({...signUpForm, confirmPassword: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="パスワードを再入力"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: loading ? '#86efac' : '#16a34a',
                color: 'white',
                fontWeight: 'bold',
                padding: '0.75rem 1rem',
                borderRadius: '6px',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 200ms'
              }}
            >
              {loading ? 'サインアップ中...' : 'サインアップ'}
            </button>
          </form>
        )}

        {activeTab === 'signup' && showConfirmation && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#dbeafe',
            borderRadius: '8px'
          }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', textAlign: 'center' }}>
              メール確認
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              {pendingEmail} に確認コードを送信しました
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                確認コード
              </label>
              <input
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="6桁の確認コードを入力"
                maxLength={6}
                required
              />
            </div>
            <button
              onClick={handleConfirmSignUp}
              disabled={loading || !confirmationCode}
              style={{
                width: '100%',
                backgroundColor: loading || !confirmationCode ? '#86efac' : '#16a34a',
                color: 'white',
                fontWeight: 'bold',
                padding: '0.75rem 1rem',
                borderRadius: '6px',
                border: 'none',
                cursor: loading || !confirmationCode ? 'not-allowed' : 'pointer',
                transition: 'background-color 200ms'
              }}
            >
              {loading ? '確認中...' : '確認'}
            </button>
          </div>
        )}

        <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.875rem', color: '#9ca3af' }}>
          <p>財務分析システム</p>
        </div>
      </div>
    </div>
  );
}
